<template>
  <b-modal
    id="import-from-image"
    v-model="isOpen"
    title="Import image"
    centered
    size="lg"
    @ok="handleOkBtn"
    @hide="handleModalClose"
  >
    <Multiselect
      v-model="selectedLayout"
      :options="layouts"
      :loading="isLoading"
      searchable
      multiple
      :custom-label="customLabel"
      track-by="id"
      class="mb-3"
      placeholder="Add to existing layout (optional)"
      @search-change="searchCustomerLayouts"
      @select="(option) => selectedLayout = [option]"
    >
      <template
        v-if="layouts.length < layoutsTotalCount"
        #afterList
      >
        <div class="load-more-btn">
          <b-button
            :disabled="isLoading"
            variant="outline-primary"
            @click="getCustomerLayouts"
          >
            Load More
          </b-button>
        </div>
      </template>
    </Multiselect>
    <b-form-radio-group
      v-model="selectedStrategy"
      name="select-strategy"
      :options="options"
    />
    <SelectTemplateBanner
      v-if="selectedStrategy === IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY"
      v-model="selectedTemplates"
      class="mt-3"
      :customers="customers"
    />
    <b-form-group
      v-if="!selectedLayout.length"
      class="mt-3"
      label="New layout name"
    >
      <b-form-input
        v-model="layoutName"
      />
    </b-form-group>
    <b-form-group
      class="mt-3"
    >
      <b-form-checkbox
        v-model="createLayers"
        switch
      >
        <span class="font-weight-bold">Create Layers</span>
        <small class="text-muted d-block">
          Use AI to segment the image into separate layers (products, background, text)
        </small>
      </b-form-checkbox>
    </b-form-group>

    <div
      v-if="!createLayers"
      class="advanced-section"
    >
      <button
        type="button"
        class="advanced-toggle"
        :aria-expanded="isAdvancedOpen"
        @click.prevent="isAdvancedOpen = !isAdvancedOpen"
      >
        <b-icon
          icon="chevron-down"
          class="advanced-toggle__chevron"
          :class="{ 'is-open': isAdvancedOpen }"
        />
        Advanced
      </button>

      <b-collapse v-model="isAdvancedOpen">
        <div class="advanced-option">
          <ResizeSizeInput
            v-model="resizeSizes"
            label="Resize Sizes"
          />
        </div>
      </b-collapse>
    </div>
    <template #modal-footer="{ cancel }">
      <div class="d-flex">
        <b-button
          @click="cancel"
        >
          Cancel
        </b-button>
        <div class="mx-1" />
        <b-button
          variant="primary"
          :disabled="!importAvailable || isImporting"
          @click="handleOkBtn"
        >
          <b-spinner
            v-if="isImporting"
            small
          />
          OK
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {EVENTS, importFromImageEventBroker} from "@frontend/group/import-from-image";
import Customer from "@frontend/services/api/customer";
import SelectTemplateBanner from "@frontend/components/common/atoms/SelectTemplateBanner.vue";
import Layout from "@frontend/services/api/layout";
import {
  IMPORT_AND_CREATE_STRATEGY,
  IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY
} from "@frontend/group/modules/import-template-from-psd/import-from-psd-new-template";
import Multiselect from "vue-multiselect";
import LayoutAPI from "@frontend/services/api/layout";
import {startCreateLayers} from "@frontend/group/modules/create-layers";
import { segmentationQueue } from "@frontend/group/modules/image-segmentation/queue";
import ResizeSizeInput from "@frontend/components/common/atoms/ResizeSizeInput.vue";
import {POST_ACTION_RESIZE_DATA_KEY} from "@frontend/constants/post-actions";

let timer;
const SETTINGS_STORAGE_KEY = 'JPG-PNG-import-modal-settings'

export default {
  name: "ImportFromImage",
  components: {ResizeSizeInput, Multiselect, SelectTemplateBanner},
  data: () => ({
    isOpen: false,
    customers: [],
    selectedTemplates: {
      customer: null,
      templates: [],
    },
    selectedLayout: [],
    layouts: [],
    layoutsTotalCount: null,
    isLoading: false,
    page: 1,
    searchKey: null,
    layoutName: '',
    imagesToImport: [],
    isImporting: false,
    options: [
      { text: 'Create new template', value: IMPORT_AND_CREATE_STRATEGY },
      { text: 'Use existing templates', value: IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY },
    ],
    selectedStrategy: IMPORT_AND_CREATE_STRATEGY,
    createLayers: false,
    isAdvancedOpen: false,
    resizeSizes: [],
  }),
  computed: {
    importAvailable() {
      switch (this.selectedStrategy) {
        case IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY:
          return this.imagesToImport.length && (this.layoutName.length || this.selectedLayout.length) && this.selectedTemplates.templates.length
        case IMPORT_AND_CREATE_STRATEGY:
          return this.layoutName.length || this.selectedLayout.length
        default:
          return false
      }
    },
    IMPORT_AND_CREATE_STRATEGY() {
      return IMPORT_AND_CREATE_STRATEGY
    },
    IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY() {
      return IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY;
    },
  },
  mounted() {
    importFromImageEventBroker.on(EVENTS.INIT, this.init.bind(this))
  },
  methods: {
    async init(dto) {
      this.isOpen = true;
      await this.loadCustomers()
      this.imagesToImport = dto;
      await this.getCustomerLayouts()
      this.loadSettings();
    },
    async loadCustomers() {
      this.customers = (await Customer.getCustomers()).data
    },
    async handleOkBtn() {
      this.saveSettings();
      if (this.createLayers) {
        await this.handleCreateLayers();
        return;
      }

      try {
        this.isImporting = true
        const backgroundThemeImages = this.imagesToImport.map(({ original_path }) => original_path);
        const productImages = this.imagesToImport.map(({ path }) => path);
        const payload = {
          name: this.layoutName,
          selectedLayout: this.selectedLayout[0]?.id,
          strategy: this.selectedStrategy,
        }
        if (backgroundThemeImages.length > 0 && typeof backgroundThemeImages[0] === "string") {
          payload.backgroundThemeImages = backgroundThemeImages;
        }
        if (productImages.length > 0 && typeof productImages[0] === "string") {
          payload.productImages = productImages;
        }
        if (payload.strategy === IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY) {
          payload.template_ids = this.selectedTemplates.templates.map(item => item.id)
        }

        const { data } = await Layout.createLayoutAndTemplateFromImage(this.selectedTemplates?.customer?.id || 0, payload);
        if(data === 's3_file_not_found'){
          toastr.warning('This image is unavailable or corrupted');
        }else {
          const { postActionsQuery } = this.initPostActions();
          window.location.href = `/banner/${data.customer_id}/group/${data.layout_id}/show?${postActionsQuery}`;
        }

        this.isImporting = false;
        this.isOpen = false
      } catch (e) {
        toastr.error('Something went wrong while importing')
        console.error(e)
        this.isImporting = false
      }

    },

    async handleCreateLayers() {
      if (this.imagesToImport.length === 0) {
        toastr.warning('Please select an image to segment');
        return;
      }

      const customerId = this.selectedTemplates?.customer?.id || 0;

      // Several images walk through the preview one at a time and end up as one template
      // each in a single layout. One image keeps the original single-shot path below.
      if (this.imagesToImport.length > 1) {
        this.isImporting = true;
        this.isOpen = false;

        try {
          await segmentationQueue.start({
            images: this.imagesToImport,
            customerId,
            targetLayoutId: this.selectedLayout[0]?.id || null,
            layoutName: this.layoutName || null,
          });
        } catch (error) {
          console.error('Error starting image segmentation queue:', error);
          toastr.error('Failed to start image segmentation');
        } finally {
          this.isImporting = false;
        }

        return;
      }

      const imagePath = this.imagesToImport[0].original_path || this.imagesToImport[0].path;

      try {
        this.isImporting = true;
        this.isOpen = false;
        this.isImporting = false;
        await startCreateLayers({
          customerId,
          layoutId: this.selectedLayout[0]?.id || null,
          layoutName: this.layoutName || null,
          imageInfo: {path: imagePath},
        });
      } catch (e) {
        toastr.error(e.response?.data?.message || 'Something went wrong while starting segmentation');
        console.error(e);
        this.isImporting = false;
      }
    },
    handleModalClose() {},
    customLabel({ id, name }) {
      return `${id} - ${name}`;
    },
    async getCustomerLayouts() {
      this.isLoading = true;

      const { data: { layouts, totalCount } } = await LayoutAPI.getLayouts({
        params: {
          search: this.searchKey,
          page: this.page,
          limit: 20
        }
      });

      this.isLoading = false;

      this.layouts = this.layouts.concat(layouts);
      this.layoutsTotalCount = totalCount;
      this.page = this.page += 1;
    },

    searchCustomerLayouts(search) {
      if (timer) {
        clearTimeout(timer);
      }

      timer = setTimeout(() => {
        this.page = 1;
        this.searchKey = search;
        this.layouts = [];

        this.getCustomerLayouts();
      }, 500);
    },

    saveSettings() {
      const settings = {
        layoutId: this.selectedLayout[0],
        name: this.layoutName,
        strategy: this.selectedStrategy,
      }

      if (settings.strategy === IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY) {
        settings.template_ids = this.selectedTemplates.templates.map(item => item.id)
        settings.customerId = this.selectedTemplates.customer.id;
      }

      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings))

    },
    loadSettings() {
      if (!localStorage.getItem(SETTINGS_STORAGE_KEY)) {
        return;
      }
      const settings = JSON.parse(localStorage.getItem(SETTINGS_STORAGE_KEY))
      if (settings.layoutId) {
        this.selectedLayout = [settings.layoutId];
      }
      this.layoutName = settings.name;
      this.selectedStrategy = settings.strategy

      if (settings.strategy === IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY) {
        this.selectedTemplates.customer = this.customers.find(customer => customer.id === settings.customerId)
        this.selectedTemplates.preselectedTemplateIds = settings.template_ids;
      }
    },
    initPostActions() {
      if (this.createLayers) {
        return {
          postActionsQuery: ''
        };
      }
      
      const postActions = [];
      
      if (this.resizeSizes.length > 0) {
        localStorage.setItem(POST_ACTION_RESIZE_DATA_KEY, JSON.stringify({
          sizes: this.resizeSizes
        }));
        postActions.push('resize');
      }

      const postActionsQuery = postActions
        .map(action => `post-actions=${encodeURIComponent(action)}`)
        .join('&')
      
      return {
        postActionsQuery
      }
    }
  }
}
</script>

<style scoped lang="scss">
.advanced-section {
  margin-top: 8px;
}

.advanced-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  background: none;
  border: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  cursor: pointer;

  &:hover { color: #0f172a; }

  &__chevron {
    transition: transform 0.2s ease;
    transform: rotate(-90deg);

    &.is-open { transform: rotate(0); }
  }
}

.advanced-option {
  margin-top: 12px;
  padding: 20px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 12px;

  label {
    display: block;
    margin-bottom: 12px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #64748b;
  }
}
</style>
